import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/utils.js';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly title?: string;
  readonly children: ReactNode;
}

/**
 * Base surface. Restyled onto semantic tokens (audit P0) — bg-surface +
 * 1px border + soft shadow replaces the old cream slab with a 2px sun
 * border, so cards work in both themes and every accent.
 */
export function Card({ className, title, children, ...rest }: CardProps) {
  return (
    <div
      className={cn('rounded-2xl border border-border bg-surface shadow-sm', className)}
      {...rest}
    >
      {title ? <CardTitle>{title}</CardTitle> : null}
      {children}
    </div>
  );
}

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1 p-5 pb-0', className)} {...rest} />;
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('font-display text-h3 font-bold text-fg', className)} {...rest} />;
}

export function CardDescription({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-fg-muted', className)} {...rest} />;
}

export function CardContent({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5', className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center gap-3 border-t border-border p-5', className)}
      {...rest}
    />
  );
}
